import { create } from "zustand"

import {PERMISSIONS, ROLES, Permission} from "@/lib/auth/roles";
import {canUserEditRiskAndOpportunities, hasApplicationFeature, isFirmViewOnNetworkPortal} from "@/lib/utils";
import {Identity} from "@/types/identity";
import {Token} from "@/types/token"
import {GUIDE_TOP_RATED_ADVISERS} from "@/utils/constants";

type TokenStore = {
  token: Token | null,
  permissions: Permission[],
  setToken: (token: Token) => void,
  setPermissions: (identity: Identity | null) => void
}

export const useToken = create<TokenStore>((set, get) => ({
  token: null,
  permissions: [],
  setToken: (token: Token) =>
    set({ token }),
  setPermissions: (identity: Identity | null) => {
    if (!identity) {
      set({permissions: []})
      return
    }

    const permissions: Permission[] = []
    const token = get().token
    const hasEditRisksAndOpportunities = token?.roles.includes(ROLES.ROLE_EDIT_RISKS_AND_OPPORTUNITIES) ?? false
    
    if (canUserEditRiskAndOpportunities(identity, hasEditRisksAndOpportunities)) {
      permissions.push(PERMISSIONS.CAN_EDIT_RISKS_AND_OPPORTUNITIES)
    }
    
    if (identity.network !== null) {
      permissions.push(PERMISSIONS.IS_NETWORK_VIEW)
      if (isFirmViewOnNetworkPortal(identity)) {
        permissions.push(PERMISSIONS.IS_FIRM_VIEW_ON_FIRM_OR_NETWORK)
      } else {
        permissions.push(PERMISSIONS.IS_NETWORK_VIEW_ON_NETWORK, PERMISSIONS.IS_FIRM_WIEW_OR_IS_NETWORK_VIEW_ON_NETWORK)
      }
    }

    if (identity.network === null) {
      permissions.push(PERMISSIONS.IS_FIRM_VIEW, PERMISSIONS.IS_FIRM_VIEW_ON_FIRM_OR_NETWORK, PERMISSIONS.IS_FIRM_WIEW_OR_IS_NETWORK_VIEW_ON_NETWORK)
    }

    if (identity.firmSubgroups === null || (Array.isArray(identity.firmSubgroups) && identity.firmSubgroups.length === 0)) {
      permissions.push(PERMISSIONS.IS_FULL_FIRM_OR_NETWORK_VIEW)
    }
    
    if (hasApplicationFeature(GUIDE_TOP_RATED_ADVISERS)) {
      permissions.push(PERMISSIONS.HAS_TOP_RATED_FEATURE)
    }
    
    if (identity.firmHasIntegration) {
      permissions.push(PERMISSIONS.CAN_VIEW_INVITE_REQUESTS)
    }
    
    if (identity.showVerifiedSidebar) {
      permissions.push(PERMISSIONS.SHOW_VERIFIED_SIDEBAR)
    }

    if (identity.showVerifiedStatistics) {
      permissions.push(PERMISSIONS.SHOW_VERIFIED_STATISTICS)
    }

    if (identity.isTopRated) {
      permissions.push(PERMISSIONS.IS_TOP_RATED)
    }

    if (identity.showVerifiedSidebar || identity.isTopRated) {
      permissions.push(PERMISSIONS.CAN_ACCESS_FIRM_MARKETING_TOOLS_PAGE)
    }

    const isFirmContext = identity.network === null || isFirmViewOnNetworkPortal(identity)
    const canAccessArticles = isFirmContext
      ? Boolean(identity.currentFirm?.is_active) && identity.hasVerifiedPlusFirmLicence
      : identity.networkHasFirmOnVerifiedPlusFirmLicence

    if (canAccessArticles) {
      permissions.push(PERMISSIONS.CAN_ACCESS_ARTICLES)
    }

    // AI controls set on the firm or network can switch these features off
    if (identity.isSensitiveAnswersAiEnabled) {
      permissions.push(PERMISSIONS.CAN_VIEW_SENSITIVE_ANSWERS)
    }

    if (identity.isElevationAiSummaryEnabled) {
      permissions.push(PERMISSIONS.CAN_VIEW_ELEVATION_AI_SUMMARY)
    }

    set({permissions})
  }
}))